import type { LaborCostWarning as LaborCostWarningPayload } from "../types";

interface Props {
  warning: LaborCostWarningPayload | undefined;
  isLoading?: boolean;
}

function formatPln(value: string | null, opts?: { signed?: boolean }): string {
  if (value === null) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  const formatted = new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
    // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
    useGrouping: "always",
  }).format(n);
  if (opts?.signed && n > 0) {
    return "+" + formatted;
  }
  return formatted;
}

export function LaborCostWarning({ warning, isLoading }: Props) {
  if (isLoading) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        Sprawdzanie kosztu pracy…
      </div>
    );
  }
  if (!warning) return null;

  // Brak baseline — informacja, nie ostrzeżenie
  if (warning.level === "missing_baseline") {
    return (
      <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
        <strong>Brak ustalonego kosztu pracy</strong> dla tego okresu — nie można
        porównać kwoty z baseline.
      </div>
    );
  }

  // Kwota jeszcze nie wpisana — pokaż neutralną informację o baseline
  if (warning.level === "none" && warning.submitted === null) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        Koszt pracy dla tego okresu:{" "}
        <strong>{formatPln(warning.baseline)}</strong>. Wpisz kwotę faktury, aby
        porównać.
      </div>
    );
  }

  if (warning.level === "none") {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
        <strong>Kwota zgodna z kosztem pracy.</strong>{" "}
        {warning.baseline !== null && (
          <>Koszt pracy: {formatPln(warning.baseline)}.</>
        )}
      </div>
    );
  }

  // mismatch
  return (
    <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
      <div className="font-medium">Niezgodność z kosztem pracy</div>
      <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-rose-600">Faktura:</div>
          <div className="font-semibold">{formatPln(warning.submitted)}</div>
        </div>
        <div>
          <div className="text-rose-600">Koszt pracy:</div>
          <div className="font-semibold">{formatPln(warning.baseline)}</div>
        </div>
        <div>
          <div className="text-rose-600">Różnica:</div>
          <div className="font-semibold">
            {formatPln(warning.delta_pln, { signed: true })}
          </div>
        </div>
      </div>
      <p className="mt-2 text-xs text-rose-700">
        Ostrzeżenie nie blokuje wysłania faktury. Sprawdź, czy kwota jest
        poprawna.
      </p>
    </div>
  );
}
